import { FC } from 'react'
import { ReportCaseMeta, ReportDeltaMetric } from '../../../api/report-generator-API'
import { SectionEmpty } from './Section'
import { deltaColorClass, formatFixed, formatSignedFixed } from './format'

export type ResultsTableProps = {
  rows: ReportDeltaMetric[]
  /** Optional attitude metadata keyed by caseName for the AHF/AHR/YAW columns. */
  caseMeta?: Record<string, ReportCaseMeta>
}

type Column = {
  header: string
  render: (row: ReportDeltaMetric, meta?: ReportCaseMeta) => string
  /** Render a colored delta (worse=red/better=green). */
  deltaField?: keyof ReportDeltaMetric
  higherIsWorse?: boolean
  numeric?: boolean
}

const columns: Column[] = [
  { header: 'Case', render: row => row.caseName ?? '', numeric: false },
  { header: 'Ref', render: row => row.ref_gm_id ?? '', numeric: false },
  { header: 'AHF', render: (_row, meta) => formatFixed(meta?.ahf, 4), numeric: true },
  { header: 'AHR', render: (_row, meta) => formatFixed(meta?.ahr, 4), numeric: true },
  { header: 'YAW', render: (_row, meta) => formatFixed(meta?.yaw, 4), numeric: true },
  {
    header: 'ΔCD',
    render: row => formatSignedFixed(row.delta_cd),
    deltaField: 'delta_cd',
    numeric: true
  },
  {
    header: 'ΔCL',
    render: row => formatSignedFixed(row.delta_cl),
    deltaField: 'delta_cl',
    numeric: true
  },
  {
    header: 'ΔCLF',
    render: row => formatSignedFixed(row.delta_clf),
    deltaField: 'delta_clf',
    numeric: true
  },
  {
    header: 'ΔCLR',
    render: row => formatSignedFixed(row.delta_clr),
    deltaField: 'delta_clr',
    numeric: true
  },
  {
    header: 'Δ%CLF',
    render: row => formatSignedFixed(row.delta_pct_clf, 4),
    deltaField: 'delta_pct_clf',
    numeric: true
  }
]

/** Headline coefficient delta table (CFD report §3 / Correlation "Δ vs Ref"). */
const ResultsTable: FC<ResultsTableProps> = ({ rows, caseMeta }) => {
  if (!rows.length) return <SectionEmpty />

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50 dark:bg-neutral-70">
            {columns.map(col => (
              <th
                key={col.header}
                className={`border border-gray-200 dark:border-neutral-60 px-2 py-1 font-bold ${
                  col.numeric ? 'text-right' : 'text-left'
                }`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const meta = caseMeta?.[row.caseName]
            return (
              <tr
                key={row.caseName}
                className={row.highlight ? 'bg-amber-50 dark:bg-amber-900/20' : ''}>
                {columns.map(col => {
                  const colorClass = col.deltaField
                    ? deltaColorClass(row[col.deltaField] as number, col.higherIsWorse ?? true)
                    : ''
                  return (
                    <td
                      key={col.header}
                      className={`border border-gray-200 dark:border-neutral-60 px-2 py-1 ${
                        col.numeric ? 'text-right tabular-nums' : 'text-left'
                      } ${colorClass}`}>
                      {col.render(row, meta)}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default ResultsTable
